import Decimal from "decimal.js";
import { DECIMAL_ONE } from "./decimal";
import { getYearMonthDateDayDisplay, getYearMonthDateDisplay } from "./utils";
import { getDirectorEmployee } from "../http/api/MasterAPI";
import { bool } from "yup";

export const sampleDestination = {
  customerName: "取引先名",
  shopName: "店舗名",
};

// 価格表　出荷日出力
export const getOutputShippingDate = (
  dateFrom,
  dateTo,
  outputDateFunction = getYearMonthDateDisplay
) => {
  let outputShippingDate = "";

  if (dateFrom) {
    outputShippingDate += outputDateFunction(new Date(dateFrom));
  }
  if (dateFrom && dateTo) {
    outputShippingDate += " ～ " + outputDateFunction(new Date(dateTo));
  }

  return outputShippingDate;
};

// 価格表　出荷日出力
export const getOutputShippingDateWithDay = (dateFrom, dateTo) => {
  return getOutputShippingDate(dateFrom, dateTo, getYearMonthDateDayDisplay);
};

export const getOutputDestination = (destinationInfo, shopSummary, asSendingLabel) => {
  let outputDestinationArray = [];

  if (destinationInfo.customerName) {
    outputDestinationArray.push(destinationInfo.customerName);
  }
  if (!shopSummary && destinationInfo.shopName) {
    outputDestinationArray.push(destinationInfo.shopName);
  }

  outputDestinationArray.push(asSendingLabel ? "御中" : "ご担当者様");

  return outputDestinationArray.join("　");
};

// 価格と原価値入率から原価を取得する
export const getPrimePrice = (price, primePriceMarkUpRate) => {
  let price_decimal = new Decimal(price);
  let primePriceMarkUpRate_decimal = new Decimal(primePriceMarkUpRate);

  // (原価)=(価格)/(１-原価値入率)　により、decimal型によって原価を計算して小数点以下を四捨五入
  Decimal.rounding = Decimal.ROUND_HALF_CEIL;

  let primePrice_decimal = price_decimal
    .div(DECIMAL_ONE.minus(primePriceMarkUpRate_decimal))
    .round();

  return primePrice_decimal.toNumber();
};

// 価格、原価値入率、売価値入率から売価を取得する
export const getSellingPrice = (
  price,
  primePriceMarkUpRate,
  sellingPriceMarkUpRate
) => {
  let price_decimal = new Decimal(price);
  let primePriceMarkUpRate_decimal = new Decimal(primePriceMarkUpRate);
  let sellingPriceMarkUpRate_decimal = new Decimal(sellingPriceMarkUpRate);

  // (売価)=(価格)/((1-原価値入率) * (1-売価値入率))　により、decimal型によって原価を計算して小数点以下を四捨五入
  Decimal.rounding = Decimal.ROUND_HALF_CEIL;

  let sellingPrice_decimal = price_decimal
    .div(
      DECIMAL_ONE.minus(primePriceMarkUpRate_decimal).mul(
        DECIMAL_ONE.minus(sellingPriceMarkUpRate_decimal)
      )
    )
    .round();

  return sellingPrice_decimal.toNumber();
};

/** 納品価格関係 **/

/** 納品価格加算コード(なし) */
export const DELIVERY_PRICE_ADDITION_CD_NONE = 1;
/** 納品価格加算コード(割合) */
export const DELIVERY_PRICE_ADDITION_CD_RATE = 2;
/** 納品価格加算コード(円) */
export const DELIVERY_PRICE_ADDITION_CD_YEN = 3;

/** 納品価格を加算するかどうかを納品価格加算コードから取得 */
export const getUsingDeliveryPrice = (deliveryPriceAdditionCd) =>
  deliveryPriceAdditionCd !== DELIVERY_PRICE_ADDITION_CD_NONE;

/** 納品価格 計算関数(なし) */
const getDeliveryPriceCalculationByNone = (price, amount) => 0;

/** 納品価格 計算関数(割合) */
const getDeliveryPriceCalculationByRate = (price, amount) => {
  let price_decimal = new Decimal(price);
  let amount_decimal = new Decimal(amount);

  Decimal.rounding = Decimal.ROUND_HALF_CEIL;
  let deliveryPrice_decimal = price_decimal
    .mul(DECIMAL_ONE.plus(amount_decimal))
    .round();
  return deliveryPrice_decimal.toNumber();
};

/** 納品価格 計算関数(円単位) */
const getDeliveryPriceCalculationByYen = (price, amount) => {
  let price_decimal = new Decimal(price);
  let amount_decimal = new Decimal(amount);

  Decimal.rounding = Decimal.ROUND_HALF_CEIL;
  let deliveryPrice_decimal = price_decimal.plus(amount_decimal).round();
  return deliveryPrice_decimal.toNumber();
};

/** 納品価格加算コードより、納品価格の計算関数を与える */
export const getDeliveryPriceCalculation = (deliveryPriceAdditionCd) =>
  ({
    [DELIVERY_PRICE_ADDITION_CD_NONE]: getDeliveryPriceCalculationByNone,
    [DELIVERY_PRICE_ADDITION_CD_RATE]: getDeliveryPriceCalculationByRate,
    [DELIVERY_PRICE_ADDITION_CD_YEN]: getDeliveryPriceCalculationByYen,
  }[deliveryPriceAdditionCd]);

export const CHECKED_DIRECTOR_EMPLOYEE_PARTITION = 1;
export const UNCHECK_DIRECTOR_EMPLOYEE_PARTITION = 2;
/**
 *
 * @param {担当者で分けるかどうか} directorEmployeePartitionCd
 * @returns Boolean
 */
export const getUsingDirectorEmployeePartition = (
  directorEmployeePartitionCd
) => directorEmployeePartitionCd == CHECKED_DIRECTOR_EMPLOYEE_PARTITION;

// 注文のある商品情報かを調べる
export const hasShopOrder = (priceInfo, shops) => {
  if (!priceInfo.shopOrderInfo) {
    return false;
  }

  return priceInfo.shopOrderInfo.some((shopOrderData) =>
    shops.some(
      (shopInfo) =>
        shopInfo.shopId === shopOrderData.shopId &&
        shopOrderData.orderQuantity > 0
    )
  );
};

// 商品の改ページ区分を取得する
export const getProductPartitionList = (
  shopPriceInfoList,
  neccesaryProductTypePartition,
  rowNumber
) => {
  let vegetablePriceInfoList = shopPriceInfoList.filter(
    (data) => data.priceDetailTypeId === 1
  );
  let fruitPriceInfoList = shopPriceInfoList.filter(
    (data) => data.priceDetailTypeId === 2
  );
  let spotPriceInfoList = shopPriceInfoList.filter(
    (data) => data.priceDetailTypeId === 3
  );

  let productPartitionList = neccesaryProductTypePartition
    ? [vegetablePriceInfoList, fruitPriceInfoList, spotPriceInfoList]
    : [
        [
          ...vegetablePriceInfoList,
          ...fruitPriceInfoList,
          ...spotPriceInfoList,
        ],
      ];

  //同じ商品でページ区切りの防ぐ
  if (neccesaryProductTypePartition) {
    let newProductPartitionList = [];

    productPartitionList = productPartitionList.map((dataList) => {
      let temporaryArray = [];
      dataList.forEach((data, index) => {
        //裏に同じ商品IDを数える
        let count = 0;
        let nextIndex = 0;
        if (index < dataList.length - 1) {
          //裏にまだ同じ商品IDなら引き続く数える　：　例え：大根は４個続けてある
          while (data.productId === dataList[index + nextIndex].productId) {
            count++;
            nextIndex++;
            //一番最後の対象までループしたら終了する
            if (index + nextIndex >= dataList.length - 1) break;
          }
        }

        //まだ同じページで表示可能なら本ページに追加
        if (temporaryArray.length + count <= rowNumber) {
          temporaryArray.push(data);
        }
        //もうページの限界で、次のページ移動しないといけないとき
        else {
          newProductPartitionList.push(temporaryArray);
          temporaryArray = [data];
        }
      });
      //最後ページをリストに追加
      newProductPartitionList.push(temporaryArray);
    });
    productPartitionList = newProductPartitionList;
  }

  return productPartitionList;
};

export const filterPartition = (shopPriceInfoList, pageParams, rowNumber) => {
  const { 
    directorEmployeePartition,
    productTypePartition,
    columnNumber 
  } = pageParams;
  
  let finalProductPartitionList = [];

  let processedList = shopPriceInfoList;

  if (directorEmployeePartition) {
    const groupByDirector = shopPriceInfoList.reduce((acc, item) => {
      acc[item.directorEmployeeId] = [
        ...(acc[item.directorEmployeeId] || []),
        item,
      ];
      return acc;
    }, {});
    processedList = Object.values(groupByDirector);
  } else {
    processedList = [shopPriceInfoList];
  }

  processedList.forEach((groupList) => {
    const separateByProductType = getProductPartitionList(
      groupList,
      productTypePartition,
      rowNumber
    );
    if (
      Array.isArray(separateByProductType) &&
      separateByProductType.length > 0
    ) {
      separateByProductType.forEach((listSeparatedByProductType) => {
        if (
          Array.isArray(listSeparatedByProductType) &&
          listSeparatedByProductType.length > 0
        ) {
          finalProductPartitionList.push(listSeparatedByProductType);
        }
      });
    }
    if (directorEmployeePartition) {
      const notEnough = finalProductPartitionList.length % columnNumber;
      for (let i = 0; i < notEnough; i++) {
        finalProductPartitionList.push([]);
      }
    }
  });
  return finalProductPartitionList;
};

/**
 * ページ数文字列のバリデーションパターン(正規表現)
 */
const PAGE_RANGE_PATTERN = /^([1-9][0-9]*(-([1-9][0-9]*){0,1}){0,1}|-[1-9][0-9]*)$/;

/**
 * ページ数指定文字列のバリデーション
 * @param {string} str ページ数指定文字列
 * @returns trueならバリデーション通過
 */
export const validatePrintPageStr = (str) => {
  const trimmedPrintPageStr = str && str.replaceAll(" ", "");

  if (!trimmedPrintPageStr || trimmedPrintPageStr == "") {
    return true;
  }	

  const pageRangeStrs = trimmedPrintPageStr.split(",");
    
  return pageRangeStrs.every((r) => PAGE_RANGE_PATTERN.test(r))
}

/**
 * ページ数文字列と総ページ数から印刷するページ数の配列を取得する
 * @param {string} printPageStr ページ数指定文字列
 * @param {number} 前ページ数
 * @returns 印刷ページ配列
 */
export const getPrintPages = (printPageStr, totalPageNum) => {

  if (!validatePrintPageStr(printPageStr)) {
    return [];
  }

  const pageNumArray = Array.from(new Array(totalPageNum)).map((_, i) => i + 1);
  const trimmedPrintPageStr = printPageStr && printPageStr.replaceAll(" ", "");
  const printPagesSet = new Set();

  if (!trimmedPrintPageStr || trimmedPrintPageStr == "") {
    return pageNumArray;
  }	

  const pageRangeStrs = trimmedPrintPageStr.split(",");

  pageRangeStrs.forEach((r) => {
    
    const rangeArray = r.split("-").map((s) => parseInt(s));

    if (rangeArray.length == 1) {
      rangeArray.push(rangeArray[0]);
    }
    if (isNaN(rangeArray[0])) {
      rangeArray[0] = pageNumArray[0]
    }
    if (isNaN(rangeArray[1])) {
      rangeArray[1] = pageNumArray[pageNumArray.length - 1]
    }
    if (rangeArray[0] > rangeArray[1]) {
      rangeArray.reverse();
    }

    pageNumArray.filter((p) => p >= rangeArray[0] && p <= rangeArray[1])
      .forEach((p) => printPagesSet.add(p))
  })

  return Array.from(printPagesSet).sort((a, b) => a - b);
}
